
import { useRef } from "react";
import { Plus, Trash2 } from "lucide-react";
import NumberInput from "../input/NumberInput";
import CurrencyInput from "../input/CurrencyInput";
import Combobox, { type ComboboxOption } from "../input/Combobox";
import { getProducts } from "../../api/productApi";

export interface ItemRow {
  id?: number;
  description: string;
  qty: number;
  unit: string;
  price: number;
  total: number;
  /** Optional link back to the product catalog. Null/undefined = manual/custom item. */
  product_id?: number | null;
}

interface DocumentItemTableProps {
  items: ItemRow[];
  onChange: (items: ItemRow[]) => void;
}

const EMPTY_ROW: ItemRow = {
  description: "",
  qty: 1,
  unit: "",
  price: 0,
  total: 0,
  product_id: null,
};

const fetchProductOptions = async (
  search: string
): Promise<ComboboxOption[]> => {
  const response = await getProducts({
    page: 1,
    limit: 20,
    search,
  });

  const list: Array<{
    id: number;
    productName: string;
    category?: string | null;
  }> = response?.data?.productList ?? [];

  return list.map((product) => ({
    id: product.id,
    label: product.productName,
    sublabel: product.category ?? undefined,
  }));
};

export default function DocumentItemTable({
  items,
  onChange,
}: DocumentItemTableProps) {
  const descRefs = useRef<Array<HTMLInputElement | null>>([]);

  const updateRow = <K extends keyof ItemRow>(
    index: number,
    key: K,
    value: ItemRow[K]
  ) => {
    const next = [...items];

    next[index] = {
      ...next[index],
      [key]: value,
    };

    onChange(next);
  };

  const addRow = (focusAfter = false) => {
    onChange([
      ...items,
      {
        ...EMPTY_ROW,
      },
    ]);

    if (focusAfter) {
      requestAnimationFrame(() => {
        descRefs.current[items.length]?.focus();
      });
    }
  };

  const removeRow = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const handlePriceKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();

      if (index === items.length - 1) {
        addRow(true);
      } else {
        descRefs.current[index + 1]?.focus();
      }
    }
  };

  return (
    <div>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="w-8 pb-2" />

            <th className="pb-2 font-medium">Description</th>

            <th className="w-20 pb-2 font-medium">QTY</th>

            <th className="w-24 pb-2 font-medium">Unit</th>

            <th className="w-36 pb-2 text-right font-medium">Price</th>

            <th className="w-36 pb-2 text-right font-medium">Total</th>

            <th className="w-10 pb-2" />
          </tr>
        </thead>

        <tbody>
          {items.map((item, index) => (
            <tr key={index} className="border-t border-gray-100">
              {/* Drag indicator */}
              <td className="py-2 pr-2 text-gray-300">
                <div className="w-1" />
              </td>

              {/* Description / Product */}
              <td className="py-2 pr-2">
                <div className="flex items-center gap-1.5">
                  <Combobox
                    inputRef={(el) => {
                      descRefs.current[index] = el;
                    }}
                    inputValue={item.description}
                    onInputChange={(value) => {
                      const next = [...items];

                      next[index] = {
                        ...next[index],
                        description: value,
                        // Free typing detaches the row
                        // from the catalog.
                        product_id: null,
                      };

                      onChange(next);
                    }}
                    onSelectOption={(option) => {
                      const next = [...items];

                      next[index] = {
                        ...next[index],
                        description: option.label,
                        product_id: Number(option.id),
                      };

                      onChange(next);
                    }}
                    fetchOptions={fetchProductOptions}
                    placeholder="Search product..."
                    wrapperClassName="flex-1"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
                    aria-label="Description / search product"
                  />

                  {item.product_id ? (
                    <span
                      title="Linked to product catalog"
                      className="shrink-0 rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
                      Linked
                    </span>
                  ) : null}
                </div>
              </td>

              {/* QTY */}
              <td className="py-2 pr-2">
                <NumberInput
                  value={item.qty}
                  onChange={(v) => updateRow(index, "qty", v)}
                  required
                  aria-label="Quantity"
                />
              </td>

              {/* Unit */}
              <td className="py-2 pr-2">
                <select
                  value={item.unit}
                  onChange={(e) => updateRow(index, "unit", e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-400"
                  aria-label="Unit">
                  <option value="">Select unit</option>
                  <option value="Pcs">Pcs</option>
                  <option value="Kg">Kg</option>
                  <option value="Gram">Gram</option>
                  <option value="Pack">Pack</option>
                  <option value="Box">Box</option>
                  <option value="Set">Set</option>
                  <option value="Unit">Unit</option>
                  <option value="Liter">Liter</option>
                  <option value="Meter">Meter</option>
                  <option value="Lusin">Lusin</option>
                  <option value="Roll">Roll</option>
                </select>
              </td>

              {/* Price */}
              <td className="py-2 pr-2">
                <CurrencyInput
                  value={item.price}
                  onChange={(v) => updateRow(index, "price", v)}
                  required
                  onKeyDown={(e) => handlePriceKeyDown(e, index)}
                  aria-label="Price"
                />
              </td>

              {/* Total */}
              <td className="py-2 pr-2">
                <CurrencyInput
                  value={item.qty * item.price}
                  readOnly
                  aria-label="Row total"
                />
              </td>

              {/* Delete */}
              <td className="py-2 text-center">
                <button
                  type="button"
                  onClick={() => removeRow(index)}
                  className="text-red-500 hover:text-red-700"
                  aria-label="Remove item">
                  <Trash2 size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Add Item */}
      <button
        type="button"
        onClick={() => addRow(true)}
        className="mt-3 flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700">
        <Plus size={16} />
        Add item
      </button>
    </div>
  );
}

